import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Loader2, Sparkles } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'
import type { CalendarEntryInput, CalendarOccurrence, ProactiveSettings } from '@/types/marketingProactive'
import { dateRange, daysAway, MOMENT_KIND_LABEL } from './proactiveMeta'
import { useBuildKit } from './useBuildKit'

interface Props {
  occurrence: CalendarOccurrence | null
  onOpenChange: (open: boolean) => void
  hiddenMoments: string[]
  settings?: ProactiveSettings
}

/**
 * One calendar moment: what it is, a kit button, and the owner's edits —
 * confirm a lunar date, change the lead time, hide it this year or for good.
 */
export function MomentSheet({ occurrence, onOpenChange, hiddenMoments, settings }: Props) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const build = useBuildKit()
  const o = occurrence
  const [title, setTitle] = useState('')
  const [starts, setStarts] = useState('')
  const [ends, setEnds] = useState('')
  const [lead, setLead] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!o) return
    setTitle(o.title)
    setStarts(o.starts_on)
    setEnds(o.ends_on)
    setLead(String(o.lead_days))
    setNote(o.note ?? '')
  }, [o])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['marketing', 'calendar'] })
    queryClient.invalidateQueries({ queryKey: ['marketing', 'home'] })
    queryClient.invalidateQueries({ queryKey: ['marketing', 'proactive-settings'] })
  }
  const onErr = (err: unknown) =>
    toast({ title: 'Could not save', description: describeApiError(err).message, variant: 'destructive' })

  const save = useMutation({
    mutationFn: (input: CalendarEntryInput) => apiClient.updateCalendarOccurrence(o!.id, input),
    onSuccess: () => {
      invalidate()
      toast({ title: 'Calendar updated' })
    },
    onError: onErr,
  })
  const reset = useMutation({
    mutationFn: () => apiClient.deleteCalendarOccurrence(o!.id),
    onSuccess: () => {
      invalidate()
      toast({ title: o?.source === 'custom' ? 'Moment deleted' : 'Back to the default dates' })
      onOpenChange(false)
    },
    onError: onErr,
  })
  const neverShow = useMutation({
    mutationFn: async (hide: boolean) => {
      if (!settings || !o) return
      const next = hide
        ? Array.from(new Set([...hiddenMoments, o.key]))
        : hiddenMoments.filter((k) => k !== o.key)
      await apiClient.updateProactiveSettings({ ...settings, hidden_moments: next })
    },
    onSuccess: (_, hide) => {
      invalidate()
      toast({ title: hide ? `${o?.title} hidden for good` : `${o?.title} is back` })
      if (hide) onOpenChange(false)
    },
    onError: onErr,
  })

  if (!o) return <Sheet open={false} onOpenChange={onOpenChange} />

  const leadNum = Number(lead)
  const leadValid = Number.isInteger(leadNum) && leadNum >= 0 && leadNum <= 90
  const datesValid = Boolean(starts) && (!ends || ends >= starts)
  const isCustom = o.source === 'custom'
  const neverShown = hiddenMoments.includes(o.key)

  const saveEdits = () => {
    if (isCustom) {
      save.mutate({ title, starts_on: starts, ends_on: ends || starts, lead_days: leadNum, note })
    } else {
      save.mutate({
        starts_on: starts !== o.starts_on || o.overridden ? starts : null,
        ends_on: ends !== o.ends_on || o.overridden ? ends : null,
        lead_days: leadNum,
        hidden: o.hidden,
      })
    }
  }

  return (
    <Sheet open={Boolean(o)} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{o.title}</SheetTitle>
          <SheetDescription>
            {MOMENT_KIND_LABEL[o.kind] ?? o.kind} · {dateRange(o.starts_on, o.ends_on)} · {daysAway(o.days_until)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {o.approximate && (
            <p className="rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
              This date is an estimate from the Islamic calendar. Once the moon is sighted, set the confirmed date below.
            </p>
          )}
          {o.idea && (
            <div>
              <p className="text-sm font-medium">Idea</p>
              <p className="text-sm text-muted-foreground">{o.idea}</p>
            </div>
          )}
          {o.note && !isCustom && <p className="text-sm text-muted-foreground">{o.note}</p>}

          {o.promote && !o.hidden && (
            o.kit_id ? (
              <Button asChild className="w-full">
                <Link to="/admin/marketing/kits" search={{ kit: o.kit_id }}>
                  {o.kit_status === 'launched' ? 'Open the live kit' : 'Open the kit'}
                </Link>
              </Button>
            ) : (
              <Button className="w-full" disabled={build.isPending} onClick={() => build.mutate({ occurrence_id: o.id })}>
                {build.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {build.isPending ? 'Drafting the kit…' : 'Build a kit for this'}
              </Button>
            )
          )}

          <div className="space-y-3 border-t pt-4">
            <p className="text-sm font-medium">{isCustom ? 'Edit moment' : 'Dates and reminder'}</p>
            {isCustom && (
              <div className="space-y-1.5">
                <Label htmlFor="m-title">Title</Label>
                <Input id="m-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="m-starts">Starts</Label>
                <Input id="m-starts" type="date" value={starts} onChange={(e) => setStarts(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="m-ends">Ends</Label>
                <Input id="m-ends" type="date" value={ends} min={starts} onChange={(e) => setEnds(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-lead">Remind me this many days before</Label>
              <Input id="m-lead" type="number" min={0} max={90} value={lead} onChange={(e) => setLead(e.target.value)} />
            </div>
            {isCustom && (
              <div className="space-y-1.5">
                <Label htmlFor="m-note">Note</Label>
                <Textarea id="m-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={saveEdits} disabled={save.isPending || !leadValid || !datesValid || (isCustom && !title.trim())}>
                Save
              </Button>
              {(o.overridden || isCustom) && (
                <Button variant="ghost" onClick={() => reset.mutate()} disabled={reset.isPending}>
                  {isCustom ? 'Delete moment' : 'Reset to default'}
                </Button>
              )}
            </div>
          </div>

          {!isCustom && (
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Hide this year</p>
                  <p className="text-xs text-muted-foreground">No reminder for this one date.</p>
                </div>
                <Switch
                  checked={o.hidden && !neverShown}
                  disabled={neverShown || save.isPending}
                  onCheckedChange={(v) => save.mutate({ starts_on: o.overridden ? o.starts_on : null, ends_on: o.overridden ? o.ends_on : null, lead_days: o.lead_days, hidden: v })}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Never show {o.title}</p>
                  <p className="text-xs text-muted-foreground">Hides it every year. Undo in Settings.</p>
                </div>
                <Switch checked={neverShown} disabled={!settings || neverShow.isPending} onCheckedChange={(v) => neverShow.mutate(v)} />
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
